"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { FormState, VariantConfig } from "../types";
import { INITIAL_FORM_STATE } from "../constants";
import { UseForm3Props } from "./types";

export const useFormState = ({ selectedSection, pageId, pageComponentId, variantDetails }: UseForm3Props & { variantDetails?: any }) => {
  const [formState, setFormState] = useState<FormState>({
    ...INITIAL_FORM_STATE,
    expandedCategoryIds: new Set(),
    expandedNestedIds: new Set(),
  });

  const prevPageComponentIdRef = useRef<{ pageComponentId?: string; componentId?: string }>({});
  const prevSectionIdRef = useRef<string | undefined>(undefined);

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.user?.user);

  const variantConfig = variantDetails as VariantConfig | null; // Use prop instead of importing

  const reduxState = useAppSelector((state) =>
    variantConfig?.selector ? variantConfig.selector(state.page as any) : null
  ) as Record<string, any> | null;

  // Memoized config values
  const componentFieldsConfig = useMemo(
    () => variantConfig?.componentFields || [],
    [variantConfig?.componentFields]
  );

  const categoryFieldsConfig = useMemo(
    () => (variantConfig as any)?.categoryFieldsConfig || [],
    [(variantConfig as any)?.categoryFieldsConfig]
  );

  const itemFieldsConfig = useMemo(
    () => (variantConfig as any)?.itemTableFieldsConfig || [],
    [(variantConfig as any)?.itemTableFieldsConfig]
  );

  const nestedItemFieldsConfig = useMemo(
    () =>
      (variantConfig as any)?.nestedItemFieldsConfig ||
      (variantConfig as any)?.semesterFieldsConfig ||
      (variantConfig as any)?.categoryFieldsConfig ||
      [],
    [
      (variantConfig as any)?.nestedItemFieldsConfig,
      (variantConfig as any)?.semesterFieldsConfig,
      (variantConfig as any)?.categoryFieldsConfig,
    ]
  );

  // Reset only when the user selects a different page_component (section id / pageComponentId).
  // Do not reset when selectedSection.componentId alone changes — prepareSectionPreviewData / onSave
  // updates that on every field change for preview, and resetting here was wiping the form on the first keystroke.
  useEffect(() => {
    const sectionChanged = prevSectionIdRef.current !== selectedSection.id;
    const pageComponentChanged =
      prevPageComponentIdRef.current?.pageComponentId !== pageComponentId;

    if (sectionChanged || pageComponentChanged) {
      prevSectionIdRef.current = selectedSection.id;
      prevPageComponentIdRef.current = {
        pageComponentId,
        componentId: selectedSection?.componentId,
      };
      setFormState({
        ...INITIAL_FORM_STATE,
        expandedCategoryIds: new Set(),
        expandedNestedIds: new Set(),
      });
      return;
    }

    prevPageComponentIdRef.current = {
      pageComponentId,
      componentId: selectedSection?.componentId,
    };
  }, [selectedSection?.id, selectedSection?.componentId, pageComponentId]);

  return {
    formState,
    setFormState,
    dispatch,
    variantConfig,
    currentUser,
    reduxState,
    componentFieldsConfig,
    categoryFieldsConfig,
    itemFieldsConfig,
    nestedItemFieldsConfig,
    prevPageComponentIdRef,
    prevSectionIdRef,
  };
};

